import React, { useState, useMemo, useEffect } from 'react';
import ReactDOM from 'react-dom';
import classNames from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSearchBar } from './useSearchBar';
import { IconSearchSecond } from '../../components';
import LocationSelector from './LocationSelector';
import CalendarSelector from './CalendarSelector';
import CategorySelector from './CategorySelector';

import css from './MainFiltersForm.css';

const MobileSearchBar = props => {
  const {
    pageName = 'SearchPage',
    categoriesSuggestions = [],
    categories = [],
    latestSearches = [],
  } = props;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const intl = useIntl();

  const logic = useSearchBar(pageName);

  const {
    formData,
    activeSection,
    setActiveSection,
    recentWhat,
    recentWhere,
    handleDateSelect,
    handleLocationSelect,
    handleCategorySelect,
    handleWhatInputChange,
    handleLocationInputChange,
    handleClearWhat,
    handleClearWhere,
    handleClearDates,
    handleSubmit,
    handleClearAll,
    handleClearRecentWhat,
    handleClearRecentWhere,
  } = logic;

  const whatPlaceholder = intl.formatMessage({ id: 'MarketplaceSearchBar.whatPlaceholder' });
  const wherePlaceholder = intl.formatMessage({ id: 'MarketplaceSearchBar.wherePlaceholder' });
  const whenPlaceholder = intl.formatMessage({ id: 'MarketplaceSearchBar.datesPlaceholder' });

  const dateDisplay = useMemo(() => {
    const { startDate, endDate } = formData;
    if (!startDate) return whenPlaceholder;
    const format = date => date.toLocaleDateString(intl.locale, { day: 'numeric', month: 'short' });
    if (startDate && !endDate) return format(startDate);
    if (startDate && endDate) return `${format(startDate)} - ${format(endDate)}`;
    return whenPlaceholder;
  }, [formData.startDate, formData.endDate, intl.locale, whenPlaceholder]);

  const getTriggerLabel = () => {
    const { what, where, startDate, endDate } = formData;
    const parts = [];

    if (what && what.trim().length > 0) parts.push(what);
    if (where && where.trim().length > 0) parts.push(where);

    if (startDate && endDate) {
      const format = date =>
        date.toLocaleDateString(intl.locale, { day: 'numeric', month: 'short' });
      parts.push(`${format(startDate)} - ${format(endDate)}`);
    }

    if (parts.length === 0) return <FormattedMessage id="MarketplaceSearchBar.search" />;

    return parts.join('\n');
  };

  const handleClose = () => {
    setActiveSection(null);
    setIsModalOpen(false);
  };

  const handleFormSubmit = e => {
    handleSubmit(e);
    handleClose();
  };

  const handleMobileClearWhat = e => {
    e.stopPropagation();
    handleClearWhat(e, null);
  };

  const handleMobileClearWhere = e => {
    e.stopPropagation();
    handleClearWhere(e, null);
  };

  const handleMobileClearDates = e => {
    e.stopPropagation();
    handleClearDates(e, null);
  };

  useEffect(() => {
    if (isModalOpen && window.innerWidth < 769) {
      const originalStyle = window.getComputedStyle(document.body).overflow;
      document.body.style.overflow = 'hidden';

      return () => {
        document.body.style.overflow = originalStyle;
      };
    }
  }, [isModalOpen]);

  if (!isModalOpen) {
    return (
      <button className={css.searchModalTrigger} onClick={() => setIsModalOpen(true)}>
        <div className={css.searchTriggerWrapper}>
          <IconSearchSecond
            className={css.searchIcon}
            color={'#717171'}
            rootClassName={css.searchModalIcon}
          />
          <span className={css.searchText}>{getTriggerLabel()}</span>
        </div>
      </button>
    );
  }

  return (isModalOpen && 
    <Modal className={css.modalOverlay} onClose={handleClose}>
      <div className={css.modalContent}>
        <div className={css.modalCloseBtnWrapper}>
          <b>
            <FormattedMessage id="MarketplaceSearchBar.search" />
          </b>
          <button onClick={handleClose} className={css.modalCloseBtn} aria-label="Close">
            &times;
          </button>
        </div>

        <form onSubmit={handleFormSubmit}>
          <div className={css.searchBar}>
            <div
              className={classNames(css.section, { [css.activeSection]: activeSection === 'what' })}
              onClick={() => setActiveSection('what')}
            >
              {activeSection === 'what' ? (
                <CategorySelector
                  searchQuery={formData.what}
                  suggestions={categoriesSuggestions}
                  categories={categories}
                  placeholder={whatPlaceholder}
                  onInputChange={handleWhatInputChange}
                  onClear={handleMobileClearWhat}
                  onSelect={handleCategorySelect}
                  recentWhat={recentWhat}
                  handleClearRecentWhat={handleClearRecentWhat}
                />
              ) : (
                <div className={css.inputWrapper}>
                  <div
                    className={classNames(css.valueDisplay, {
                      [css.valueSelected]: !!formData.what,
                    })}
                  >
                    {formData.what || whatPlaceholder}
                  </div>
                  {formData.what && (
                    <button type="button" className={css.clearBtn} onClick={handleMobileClearWhat}>
                      ×
                    </button>
                  )}
                </div>
              )}
            </div>

            <div
              className={classNames(css.section, {
                [css.activeSection]: activeSection === 'where',
              })}
              onClick={() => setActiveSection('where')}
            >
              {activeSection === 'where' ? (
                <LocationSelector
                  searchQuery={formData.where}
                  latestSearches={recentWhere}
                  placeholder={wherePlaceholder}
                  onInputChange={handleLocationInputChange}
                  onClear={handleMobileClearWhere}
                  onSelect={handleLocationSelect}
                  handleClearRecentWhere={handleClearRecentWhere}
                />
              ) : (
                <div className={css.inputWrapper}>
                  <div
                    className={classNames(css.valueDisplay, {
                      [css.valueSelected]: !!formData.where,
                    })}
                  >
                    {formData.where || wherePlaceholder}
                  </div>
                  {formData.where && (
                    <button type="button" className={css.clearBtn} onClick={handleMobileClearWhere}>
                      ×
                    </button>
                  )}
                </div>
              )}
            </div>

            <div
              className={classNames(css.section, { [css.activeSection]: activeSection === 'when' })}
              onClick={() => setActiveSection('when')}
            >
              <div className={css.inputWrapper}>
                <div
                  className={classNames(css.valueDisplay, {
                    [css.valueSelected]: !!formData.startDate,
                  })}
                >
                  {dateDisplay}
                </div>
                {formData.startDate && (
                  <button type="button" className={css.clearBtn} onClick={handleMobileClearDates}>
                    ×
                  </button>
                )}
              </div>
              {activeSection === 'when' && (
                <CalendarSelector
                  startDate={formData.startDate}
                  endDate={formData.endDate}
                  onSelect={handleDateSelect}
                  intl={intl}
                />
              )}
            </div>
            <div className={css.actionContainer}>
              <button type="button" className={css.clearAllBtn} onClick={handleClearAll}>
                <FormattedMessage id="MarketplaceSearchBar.clear" />
              </button>
              <button className={css.searchBtn} type="submit">
                <IconSearchSecond rootClassName={css.searchModalIcon} />
                <FormattedMessage id="MarketplaceSearchBar.search" />
              </button>
            </div>
          </div>
        </form>
      </div>
    </Modal>
  );
};

const Modal = ({ children, onClose }) => {
  useEffect(() => {
    const handleKeyDown = e => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return ReactDOM.createPortal(
    <div className={css.modalBackdrop} onClick={onClose}>
      <div className={css.modalContainer} onClick={e => e.stopPropagation()}>
        {children}
      </div>
    </div>,
    document.body
  );
};

export default MobileSearchBar;
